import { Link } from 'react-router';
import { BookOpen, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatPercent } from '@/lib/formatters';
import type { Enrollment } from '@/types';
import { CATEGORY_COLORS, CATEGORY_BORDER } from '../_labels';

export function ActiveCoursesSection({
  activeEnrollments,
  categoryLabel,
}: {
  activeEnrollments: Enrollment[];
  categoryLabel: (category: string) => string;
}) {
  const sorted = [...activeEnrollments].sort((a, b) => b.progress_percent - a.progress_percent);
  const continueEnrollment = sorted.find((e) => e.progress_percent > 0) ?? null;
  const otherEnrollments = continueEnrollment
    ? activeEnrollments.filter((e) => e.id !== continueEnrollment.id).slice(0, 3)
    : activeEnrollments.slice(0, 4);

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">
          {continueEnrollment ? 'Continua donde lo dejaste' : 'Cursos en Progreso'}
        </h2>
        {activeEnrollments.length > 0 && (
          <Link to="/courses" className="text-sm font-medium text-primary hover:underline">
            Ver todos
          </Link>
        )}
      </div>

      {activeEnrollments.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-10 text-center">
          <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            No tienes cursos en progreso actualmente.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {continueEnrollment && (
            <Link
              to={`/courses/${continueEnrollment.id}`}
              className={cn(
                'group flex flex-col gap-4 rounded-xl border border-primary/30 border-t-4 bg-primary/5 p-5 transition-shadow hover:shadow-md sm:flex-row sm:items-center',
                CATEGORY_BORDER[continueEnrollment.course.category] ?? CATEGORY_BORDER.general,
              )}
            >
              <div className="min-w-0 flex-1">
                <span
                  className={cn(
                    'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                    CATEGORY_COLORS[continueEnrollment.course.category] ?? CATEGORY_COLORS.general,
                  )}
                >
                  {categoryLabel(continueEnrollment.course.category)}
                </span>
                <h3 className="mt-1.5 text-lg font-bold text-card-foreground group-hover:text-primary">
                  {continueEnrollment.course.title}
                </h3>
                {continueEnrollment.due_date && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Vence: {formatDate(continueEnrollment.due_date)}
                  </p>
                )}
                <div className="mt-3 max-w-md">
                  <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Progreso</span>
                    <span className="font-medium">
                      {formatPercent(continueEnrollment.progress_percent)}
                    </span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${continueEnrollment.progress_percent}%` }}
                    />
                  </div>
                </div>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors group-hover:bg-primary/90">
                Continuar <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          )}

          {otherEnrollments.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {otherEnrollments.map((enrollment) => (
                <Link
                  key={enrollment.id}
                  to={`/courses/${enrollment.id}`}
                  className={cn(
                    'group rounded-xl border border-border border-t-4 bg-card p-4 transition-shadow hover:shadow-md',
                    CATEGORY_BORDER[enrollment.course.category] ?? CATEGORY_BORDER.general,
                  )}
                >
                  <h3 className="line-clamp-2 text-sm font-semibold text-card-foreground group-hover:text-primary">
                    {enrollment.course.title}
                  </h3>
                  <span
                    className={cn(
                      'mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                      CATEGORY_COLORS[enrollment.course.category] ?? CATEGORY_COLORS.general,
                    )}
                  >
                    {categoryLabel(enrollment.course.category)}
                  </span>
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Progreso</span>
                      <span className="font-medium">
                        {formatPercent(enrollment.progress_percent)}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${enrollment.progress_percent}%` }}
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
